import { createSlice } from "@reduxjs/toolkit"
import types from "../../Actions/types"
import { preSurveyMetadata } from "./preSurveyMetadata"
import { applicationMetadata } from "./applicationMetadata"
import { profileMetadata } from "./profileMetadata"
import { spouseProfileMetadata } from "../SpouseApplication/spouseProfileMetadata"
import { postSurveyMetadata } from "./postSurveyMetadata"
import * as utils from "../../Utils/reducerUtils"

const initialState = {
  showErrors: false,
  applicationMetadata,
  profileMetadata,
  preSurveyMetadata,
  postSurveyMetadata,
  initialized: false,
  userWillReLocate: false,
  submittingApplication: false,
  saving: false,
  application: {},
  applicationIsComplete: false,
  interests: [],
  fetchingInterests: false,
  spouseProfileMetadata
}

const applicationSlice = createSlice({
  name: "application",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(types.SUBMITTED_APPLICATION, (state) => {
      state.submittingApplication = false
    })

    builder.addCase(types.LOGOUT, () => ({
      showErrors: false,
      applicationMetadata,
      profileMetadata,
      preSurveyMetadata,
      postSurveyMetadata,
      initialized: false
    }))

    builder.addCase(types.TOGGLE_UNFILLED, (state) => {
      state.showErrors = true
      state.applicationMetadata = state.applicationMetadata.map((input) => ({
        ...input,
        steps: input.steps.map((step) =>
          !step.filled ? { ...step, unseenError: true } : step
        )
      }))
      state.profileMetadata = state.profileMetadata.map((input) => ({
        ...input,
        steps: input.steps.map((step) =>
          !step.filled ? { ...step, unseenError: true } : step
        )
      }))
    })

    builder.addCase(types.SUBMIT_APPLICATION, (state) => {
      state.showErrors = false
      state.submittingApplication = true
    })

    builder.addCase(types.INIT_APPLICATION, (state, action) => {
      const updatedMetaData = utils.processNavOptionsForApplication(
        action.data.application,
        state.applicationMetadata
      )
      const metaData1 =
        action.data.application && action.data.application.is_spouse === 1
          ? state.spouseProfileMetadata
            ? state.spouseProfileMetadata
            : spouseProfileMetadata
          : state.application && state.application.profileMetadata
          ? state.application.profileMetadata
          : profileMetadata

      const updatedProfileMetaData = utils.processNavOptionsforProfile(
        action.data.application,
        metaData1
      )

      state.application = action.data.application
      state.applicationMetadata = updatedMetaData
      state.applicationIsComplete = utils.allFormsFilled(updatedMetaData)
      state.profileIsComplete = utils.allFormsFilled(updatedProfileMetaData)
      state.initialized = true
      state.profileMetadata = updatedProfileMetaData
      state.userWillReLocate =
        action.data.application.address &&
        action.data.application.address.willLocate === "Yes"
    })

    builder.addCase(types.FETCHED_SPOUSE_APPLICATION, (state, action) => {
      const updatedSpouseApplicationMetaData = utils.processNavOptionsForApplication(
        action.data.application,
        state.applicationMetadata
      )
      const updatedSpouseProfileData = utils.processNavOptionsforProfile(
        action.data.application,
        state.profileMetadata
      )

      state.spouseApplication = action.data.application
      state.applicationMetadata = updatedSpouseApplicationMetaData
      state.saving = false
      state.applicationIsComplete = utils.allFormsFilled(
        updatedSpouseApplicationMetaData
      )
      state.profileIsComplete = utils.allFormsFilled(updatedSpouseProfileData)
      state.profileMetadata = updatedSpouseProfileData
      state.currentInputData = null
      state.userWillReLocate =
        action.data.application.address &&
        action.data.application.address.willLocate === "Yes"
    })

    builder.addCase(types.SET_APPLICATION_INPUT_DATA, (state, action) => {
      state.currentInputData = action.data.inputData
    })

    builder.addCase(types.LOGIN, (state) => {
      state.initialized = false
      state.showErrors = false
    })

    builder.addCase(types.EDIT_APPLICATION, (state) => {
      state.currentInputData = null
      state.saving = false
    })

    builder.addCase(types.FETCHED_APPLICATION, (state, action) => {
      const updatedApplicationMetaData = utils.processNavOptionsForApplication(
        action.data.application,
        state.applicationMetadata
      )

      const metaData2 =
        action.data.application && action.data.application.is_spouse === 1
          ? state.spouseProfileMetadata
            ? state.spouseProfileMetadata
            : spouseProfileMetadata
          : state.application && state.application.profileMetadata
          ? state.application.profileMetadata
          : profileMetadata

      const updatedProfileData = utils.processNavOptionsforProfile(
        action.data.application,
        metaData2
      )

      state.application = action.data.application
      state.applicationMetadata = updatedApplicationMetaData
      state.saving = false
      state.applicationIsComplete = utils.allFormsFilled(
        updatedApplicationMetaData
      )
      state.profileIsComplete = utils.allFormsFilled(updatedProfileData)
      state.profileMetadata = updatedProfileData
      state.currentInputData = null
      state.userWillReLocate =
        action.data.application.address &&
        action.data.application.address.willLocate === "Yes"
    })

    builder.addCase(types.SAVING_APPLICATION, (state) => {
      state.saving = true
    })

    builder.addCase(types.WILL_RELOCATE_CHANGED, (state, action) => {
      const updatedMeta = utils.processNavOptionsForApplication(
        state.application,
        state.applicationMetadata
      )

      const metaData3 =
        action.data.application && action.data.application.is_spouse === 1
          ? state.spouseProfileMetadata
            ? state.spouseProfileMetadata
            : spouseProfileMetadata
          : state.application && state.application.profileMetadata
          ? state.application.profileMetadata
          : profileMetadata

      const updatedProfileMeta = utils.processNavOptionsforProfile(
        state.application,
        metaData3
      )

      state.userWillReLocate = action.data.value
      state.applicationMetadata = updatedMeta
      state.profileMetadata = updatedProfileMeta
    })

    builder.addCase(types.UPDATE_INTERESTS, (state, action) => {
      state.interests = action.data.interests
    })

    builder.addCase(types.TOGGLE_FETCHING_INTERESTS, (state, action) => {
      state.fetchingInterests = action.data.toggle
    })

    builder.addCase(types.UPDATE_LIKERT_SPOUSE, (state, action) => {
      const { userField, value } = action.payload
      const fieldArray = userField.split(".")
      let currentState =
        state.spouseApplication && state.spouseApplication.application
          ? state.spouseApplication.application
          : {}
      for (let i = 0; i < fieldArray.length - 1; i++) {
        currentState = currentState[fieldArray[i]]
        if (!currentState) break
      }
      if (currentState) {
        currentState[fieldArray[fieldArray.length - 1]] = value
      }
    })
  }
})

export default applicationSlice.reducer
